import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import type { ExternalApiTokenWithPlaintext } from '../../lib/api/externalApiTokens';

interface RevealTokenModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The freshly-minted (or rotated) token, including its plaintext value. */
  token: ExternalApiTokenWithPlaintext | null;
  /** Verb shown in the description: 'created' for new tokens, 'rotated' for re-keyed ones. */
  mode?: 'created' | 'rotated';
}

/**
 * One-time reveal of a plaintext API token.
 *
 * The user must copy the token while this dialog is open — once dismissed it
 * is not retrievable; only rotation will yield a new plaintext value.
 */
export function RevealTokenModal({ open, onOpenChange, token, mode = 'created' }: RevealTokenModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not access the clipboard.';
      toast.error(`Copy failed: ${message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Your new API token</DialogTitle>
          <DialogDescription>
            {token?.label
              ? `Token "${token.label}" ${mode} successfully.`
              : `Token ${mode} successfully.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Yellow warning banner */}
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>Save this token now.</strong> You won't be able to see it again
              — if you lose it, rotate to get a new one.
            </p>
          </div>

          {/* Token value box */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Token</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!token}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="rounded-md border bg-muted p-3">
              <code className="block break-all font-mono text-sm">
                {token?.token ?? ''}
              </code>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
